import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, FileBarChart2 } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';

import { useGetTransactionCode } from '~entities/payment/api/get-transaction-codes';
import { PaymentStatus } from '~entities/payment/constants';
import type { BirthdayStatusFilter } from '~entities/student/api/reporting-birthdays';
import { useStudioStore } from '~entities/studios/model/studio-store';

import { useReportDownload } from '~features/payment/hooks/use-report-download';
import { useReportQueries } from '~features/payment/hooks/use-report-queries';
import { ReportCard } from '~features/payment/ui/report-card';
import { ReportInputDialog } from '~features/payment/ui/report-input-dialog';
import { ReportSection } from '~features/payment/ui/report-section';
import { type LocationOption } from '~features/shared/location-selector';

import {
  ALL_MONTHS,
  type MonthSelectorValue,
  type Report,
  reportRequiresDialog,
  reports,
  reportSections,
  ReportType,
} from './report-config';

export function PaymentReportsPage() {
  const studio = useStudioStore();

  // Currently selected report for the dialog
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Date ranges state (for reports with date range)
  const [dateRanges, setDateRanges] = useState<
    Partial<Record<ReportType, DateRange>>
  >(
    reports.reduce<Partial<Record<ReportType, DateRange>>>((acc, report) => {
      if (report.hasDateRange && report.defaultDateRange) {
        acc[report.id] = report.defaultDateRange;
      }
      return acc;
    }, {}),
  );

  // Single date state
  const [dueDate, setDueDate] = useState<Date | undefined>(new Date());

  // Session/Class state
  const [selectedSession, setSelectedSession] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedClass, setSelectedClass] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<{
    label: string;
    value: string;
  } | null>(null);

  // Location state
  const [selectedLocation, setSelectedLocation] =
    useState<LocationOption | null>(null);

  // Payment status state
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | undefined>(
    PaymentStatus.ALL,
  );

  // Days past due state
  const [daysPastDue, setDaysPastDue] = useState<number | ''>(1);

  // Stripe payment intents state
  const [stripePaymentIntents, setStripePaymentIntents] = useState<
    number | string
  >(30);

  // Transaction codes report lookback state
  const [transactionCodesLookbackDays, setTransactionCodesLookbackDays] =
    useState<number | string>(30);

  const { data: transactionCodesData } = useGetTransactionCode();
  const hasConfiguredTransactionCodes =
    (transactionCodesData ?? []).filter((code) => !code.isDeleted).length > 0;

  // Month selector state (defaults to All months / all students)
  const [selectedMonth, setSelectedMonth] =
    useState<MonthSelectorValue>(ALL_MONTHS);

  // Birthday report active/inactive status filter
  const [birthdayStatus, setBirthdayStatus] =
    useState<BirthdayStatusFilter>('all');

  const [classKeywords, setClassKeywords] = useState('');

  // Optional age filters for student reports
  const [ageAsOfDate, setAgeAsOfDate] = useState<Date | undefined>(undefined);
  const [studentMinAge, setStudentMinAge] = useState<number | ''>('');
  const [studentMaxAge, setStudentMaxAge] = useState<number | ''>('');

  // Hooks
  const queries = useReportQueries({
    studioId: studio.currentStudio?._id ?? '',
    dateRanges,
    selectedSession,
    selectedClass,
    selectedGroup,
    selectedLocation,
    daysPastDue,
    stripePaymentIntents,
    transactionCodesLookbackDays,
    dueDate,
    paymentStatus,
    selectedMonth,
    birthdayStatus,
    classKeywords,
    ageAsOfDate,
    studentMinAge,
    studentMaxAge,
  });

  const { downloadReport, loadingReportId } = useReportDownload({
    queries,
    dateRanges,
    selectedSession,
    selectedClass,
    selectedGroup,
    selectedLocation,
    daysPastDue,
    stripePaymentIntents,
    transactionCodesLookbackDays,
    dueDate,
    selectedMonth,
    birthdayStatus,
    classKeywords,
    ageAsOfDate,
    studentMinAge,
    studentMaxAge,
  });

  const handleDateRangeChange = (
    reportId: ReportType,
    dateRange: DateRange | undefined,
  ) => {
    setDateRanges((prev) => ({
      ...prev,
      [reportId]: dateRange,
    }));
  };

  const handleDownloadClick = (report: Report) => {
    if (reportRequiresDialog(report)) {
      // Reset dialog state for the new report
      setSelectedReport(report);
      setSelectedSession(null);
      setSelectedClass(null);
      setSelectedGroup(null);
      setSelectedLocation(null);
      setPaymentStatus(PaymentStatus.ALL);
      setDaysPastDue(1);
      setStripePaymentIntents(30);
      setTransactionCodesLookbackDays(30);
      setSelectedMonth(ALL_MONTHS);
      setBirthdayStatus('all');
      setClassKeywords('');
      setAgeAsOfDate(undefined);
      setStudentMinAge('');
      setStudentMaxAge('');
      // Set date range to default for this report
      if (report.hasDateRange && report.defaultDateRange) {
        handleDateRangeChange(report.id, report.defaultDateRange);
      }
      setDialogOpen(true);
    } else {
      // Direct download for reports without inputs
      void downloadReport(report.name, report.id);
    }
  };

  const handleDialogDownload = () => {
    if (selectedReport) {
      void downloadReport(selectedReport.name, selectedReport.id);
      setDialogOpen(false);
    }
  };

  return (
    <div className='min-h-screen min-w-0 bg-background'>
      {/* Header */}
      <div className='border-b border-border bg-card'>
        <div className='mx-auto max-w-6xl p-4 sm:p-6'>
          <Link
            to='/dashboard'
            className='mb-3 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground sm:mb-4'
          >
            <CircleArrowLeft className='size-4' />
            <span className='hidden sm:inline'>Back to Dashboard</span>
            <span className='sm:hidden'>Back</span>
          </Link>

          <div className='flex items-center gap-3 sm:gap-4'>
            <div className='rounded-xl bg-primary/10 p-2 sm:p-3'>
              <FileBarChart2 className='size-5 text-primary sm:size-6' />
            </div>
            <div>
              <h1 className='text-xl font-bold text-foreground sm:text-2xl'>
                Studio Reports
              </h1>
              <p className='hidden text-xs text-muted-foreground sm:block sm:text-sm'>
                Export revenue, transactions, and analytics data
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8'>
        <div className='space-y-8 sm:space-y-10'>
          {reportSections.map((section) => (
            <ReportSection
              key={section.category}
              title={section.title}
              description={section.description}
              category={section.category}
            >
              {section.reports
                .filter((report) => {
                  if (report.id === ReportType.TransactionCodesReport) {
                    return hasConfiguredTransactionCodes;
                  }
                  return true;
                })
                .map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    isLoading={loadingReportId === report.id}
                    onDownload={() => {
                      handleDownloadClick(report);
                    }}
                  />
                ))}
            </ReportSection>
          ))}
        </div>
      </div>

      {/* Generic Report Input Dialog */}
      <ReportInputDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        report={selectedReport}
        isLoading={loadingReportId === selectedReport?.id}
        onDownload={handleDialogDownload}
        // Date range
        dateRange={selectedReport ? dateRanges[selectedReport.id] : undefined}
        onDateRangeChange={(range) => {
          if (selectedReport) {
            handleDateRangeChange(selectedReport.id, range);
          }
        }}
        // Single date
        singleDate={dueDate}
        onSingleDateChange={setDueDate}
        // Session/Class
        selectedSession={selectedSession}
        onSessionChange={setSelectedSession}
        selectedClass={selectedClass}
        onClassChange={setSelectedClass}
        selectedGroup={selectedGroup}
        onGroupChange={setSelectedGroup}
        // Location
        selectedLocation={selectedLocation}
        onLocationChange={setSelectedLocation}
        // Payment status
        paymentStatus={paymentStatus}
        onPaymentStatusChange={setPaymentStatus}
        // Days past due
        daysPastDue={daysPastDue}
        onDaysPastDueChange={setDaysPastDue}
        // Stripe payment intents
        stripePaymentIntents={stripePaymentIntents}
        onStripePaymentIntentsChange={setStripePaymentIntents}
        transactionCodesLookbackDays={transactionCodesLookbackDays}
        onTransactionCodesLookbackDaysChange={setTransactionCodesLookbackDays}
        // Month
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        // Birthday status filter
        birthdayStatus={birthdayStatus}
        onBirthdayStatusChange={setBirthdayStatus}
        classKeywords={classKeywords}
        onClassKeywordsChange={setClassKeywords}
        ageAsOfDate={ageAsOfDate}
        onAgeAsOfDateChange={setAgeAsOfDate}
        studentMinAge={studentMinAge}
        onStudentMinAgeChange={setStudentMinAge}
        studentMaxAge={studentMaxAge}
        onStudentMaxAgeChange={setStudentMaxAge}
      />
    </div>
  );
}
